import React from "react";
import SectionHeader from "../../../common/section-header/index.js";
import TextInput from "../../../common/text-input/index.js";
import Text from "../../../common/typography/text.js";
import { SegmentedControl } from "../../../common/toggle/index.js";
import {
  DISCOUNT_MODES,
  DISCOUNT_MODE_OPTIONS,
  OFFER_TYPES,
  OFFER_TYPE_OPTIONS,
  isValidDiscount,
  isValidPricePerSquare,
} from "../accountEnrollmentPricing.js";

const discountError = (value) =>
  String(value ?? "").trim() === ""
    ? "Required"
    : isValidDiscount(value)
      ? ""
      : "Enter 0–100 with up to two decimals";

const pricePerSquareError = (value) =>
  String(value ?? "").trim() === ""
    ? "Required"
    : isValidPricePerSquare(value)
      ? ""
      : "Enter an amount greater than 0 with up to two decimals";

const PricingStep = ({ pricing, selectedGroups = [], onChange }) => {
  const setValue = (patch) => onChange({ ...pricing, ...patch });

  const setGroupValue = (bucket, groupId, value) =>
    setValue({
      [bucket]: { ...pricing[bucket], [groupId]: value },
    });

  const setProductValue = (groupId, productId, value) =>
    setValue({
      ProductDiscounts: {
        ...pricing.ProductDiscounts,
        [groupId]: {
          ...(pricing.ProductDiscounts?.[groupId] || {}),
          [productId]: value,
        },
      },
    });

  const isPricePerSquare = pricing.OfferType === OFFER_TYPES.PRICE_PER_SQUARE;

  return (
    <div className="account-enrollment-step">
      <div className="account-enrollment-section">
        <SectionHeader showButton={false} title="Offer Type" />
        <SegmentedControl
          onChange={(value) => setValue({ OfferType: value })}
          options={OFFER_TYPE_OPTIONS}
          value={pricing.OfferType}
        />
      </div>

      {isPricePerSquare ? (
        <div className="account-enrollment-section">
          <SectionHeader showButton={false} title="Price Per Square" />
          <div className="account-enrollment-pricing-block">
            {selectedGroups.map((group) => (
              <div
                className="account-enrollment-pricing-row"
                key={group.ProductGroupID}
              >
                <div>
                  <Text variant="smallText">{group.ProductGroupName}</Text>
                  <div>
                    <Text color="lightText" variant="tinyText">
                      {group.ManufacturerName} · {group.QCode}
                    </Text>
                  </div>
                </div>
                <TextInput
                  errorText={pricePerSquareError(
                    pricing.GroupPricePerSquare?.[group.ProductGroupID],
                  )}
                  isRequired
                  placeholder="0.00"
                  setValue={(value) =>
                    setGroupValue(
                      "GroupPricePerSquare",
                      group.ProductGroupID,
                      value,
                    )
                  }
                  titleText="Price per square ($)"
                  value={
                    pricing.GroupPricePerSquare?.[group.ProductGroupID] ?? ""
                  }
                />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="account-enrollment-section">
            <SectionHeader showButton={false} title="Discount Mode" />
            <SegmentedControl
              onChange={(value) => setValue({ DiscountMode: value })}
              options={DISCOUNT_MODE_OPTIONS}
              value={pricing.DiscountMode}
            />
          </div>

          {pricing.DiscountMode === DISCOUNT_MODES.DEFAULT_FOR_ALL ? (
            <div className="account-enrollment-section">
              <SectionHeader
                showButton={false}
                title="Default Discount For All Groups"
              />
              <div className="account-enrollment-default-discount">
                <TextInput
                  errorText={discountError(pricing.DefaultDiscountPercentage)}
                  isRequired
                  placeholder="0.00"
                  setValue={(value) =>
                    setValue({ DefaultDiscountPercentage: value })
                  }
                  titleText="Discount (%)"
                  value={pricing.DefaultDiscountPercentage ?? ""}
                />
              </div>
              <Text color="lightText" variant="tinyText">
                Every selected product group inherits this discount.
              </Text>
            </div>
          ) : null}

          {pricing.DiscountMode === DISCOUNT_MODES.PER_PRODUCT_GROUP ? (
            <div className="account-enrollment-section">
              <SectionHeader
                showButton={false}
                title="Discount Per Product Group"
              />
              <div className="account-enrollment-pricing-block">
                {selectedGroups.map((group) => (
                  <div
                    className="account-enrollment-pricing-row"
                    key={group.ProductGroupID}
                  >
                    <div>
                      <Text variant="smallText">{group.ProductGroupName}</Text>
                      <div>
                        <Text color="lightText" variant="tinyText">
                          {group.ManufacturerName} · {group.QCode}
                        </Text>
                      </div>
                    </div>
                    <TextInput
                      errorText={discountError(
                        pricing.GroupDiscounts?.[group.ProductGroupID],
                      )}
                      isRequired
                      placeholder="0.00"
                      setValue={(value) =>
                        setGroupValue(
                          "GroupDiscounts",
                          group.ProductGroupID,
                          value,
                        )
                      }
                      titleText="Discount (%)"
                      value={
                        pricing.GroupDiscounts?.[group.ProductGroupID] ?? ""
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {pricing.DiscountMode === DISCOUNT_MODES.PER_PRODUCT_CODE ? (
            <div className="account-enrollment-section">
              <SectionHeader
                showButton={false}
                title="Discount Per Product Code"
              />
              <div className="account-enrollment-pricing-block">
                {selectedGroups.map((group) => (
                  <div
                    className="account-enrollment-pricing-group"
                    key={group.ProductGroupID}
                  >
                    <div>
                      <Text variant="smallText">{group.ProductGroupName}</Text>
                      <div>
                        <Text color="lightText" variant="tinyText">
                          {group.ManufacturerName} · {group.QCode}
                        </Text>
                      </div>
                    </div>
                    {(group.Products || []).map((product) => (
                      <div
                        className="account-enrollment-product-row"
                        key={product.ProductID}
                      >
                        <div>
                          <Text variant="smallText">
                            {product.ProductCode} — {product.ProductName}
                          </Text>
                          <div>
                            <Text color="lightText" variant="tinyText">
                              {product.Units} units · $
                              {Number(product.StandardPrice).toLocaleString(
                                "en-US",
                                { minimumFractionDigits: 2 },
                              )}
                            </Text>
                          </div>
                        </div>
                        <TextInput
                          errorText={discountError(
                            pricing.ProductDiscounts?.[group.ProductGroupID]?.[
                              product.ProductID
                            ],
                          )}
                          isRequired
                          placeholder="0.00"
                          setValue={(value) =>
                            setProductValue(
                              group.ProductGroupID,
                              product.ProductID,
                              value,
                            )
                          }
                          titleText="Discount (%)"
                          value={
                            pricing.ProductDiscounts?.[group.ProductGroupID]?.[
                              product.ProductID
                            ] ?? ""
                          }
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default PricingStep;
